import { TrackingEvent } from '@acretrader/acremaps-analytics-events';
import type { Feature, Geometry } from 'geojson';
import { useParams } from 'next/navigation';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { PLAT_MAP_URL } from '@/constants/urls';
import type { LiteConsolidatedComp } from '@/endpoints/itasca/comps/queryConsolidatedComps';
import { useThreeDController } from '@/features/3d/3DControllerProvider';
import { useDeedPlotterContext } from '@/features/boundaries/deedPlotter/provider';
import { useCustomizeLandContext } from '@/features/customizeMyLand/provider';
import {
  buildShareCodeFromFeatures,
  buildShareCodeFromSelectionWithComps,
  downloadCompsAndParcelsAsCsv,
  downloadSelectionAsCsv,
  setSavedSelectionPublic,
} from '@/features/selectionsPanel/exportMenu/utils';
import { buildEmbedLink } from '@/features/share/buildEmbedLink';
import { useUserQuotasContext } from '@/features/userQuotas/provider';
import useAssociatedComps from '@/hooks/useAssociatedComps';
import type {
  IAcresCompFeature,
  IBoundaryFeature,
  IClaimedFeature,
  ICLUFeature,
  IParcelFeature,
  IRealEstateFeature,
  ISavedFeature,
  IUploadFeature,
} from '@/models/features';
import type { ReactSetState } from '@/models/reactTypes';
import { useAnalytics } from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/auth-provider';
import { useFileManagementContext } from '@/providers/FileManagementProvider';
import type { RootState } from '@/redux/store';
import deepLink from '@/utils/deepLink';
import exportKML from '@/utils/exportKML';
import {
  unimplementedAsyncFunction,
  unimplementedFunction,
} from '@/utils/unimplementedFunction';

type SelectionsPanelContextType = {
  isPanelOpen: boolean;
  setIsPanelOpen: ReactSetState<boolean>;
  isExportMenuOpen: boolean;
  isAerialReportPanelOpen: boolean;
  setIsAerialReportPanelOpen: ReactSetState<boolean>;
  setIsExportMenuOpen: ReactSetState<boolean>;
  totalSelectionCount: number;
  selectedParcels: IParcelFeature[];
  selectedFsa: ICLUFeature[];
  selectedComps: (
    | IAcresCompFeature
    | Feature<Geometry, LiteConsolidatedComp>
  )[];
  selectedListings: IRealEstateFeature[];
  selectedBoundaries: IBoundaryFeature[];
  selectedUploads: IUploadFeature[];
  selectedDeedFeatures: any[];
  selectedCustomizations: any[] | null;
  getEmbedLink: (config?: {
    showElevation?: boolean;
    showCropHistory?: boolean;
    showSoils?: boolean;
    set3dMode?: boolean;
    showCustomizations?: boolean;
  }) => Promise<string | undefined>;
  canCopyEmbedLink: boolean;
  getDeepLinkShareUrl: () => Promise<string | undefined>;
  canCopyDeepLinkShareUrl: boolean;
  exportToCsv: () => Promise<void>;
  exportToKml: () => Promise<void>;
  selectedSubjectProperties: (
    | IParcelFeature
    | ICLUFeature
    | IBoundaryFeature
    | IUploadFeature
    | ISavedFeature
    | IClaimedFeature
  )[];
  canExportToCsv: boolean;
  canExportToKml: boolean;
  isEmbedPanelOpen: boolean;
  setIsEmbedPanelOpen: ReactSetState<boolean>;
  hasSelectedFeature: boolean;
};

const defaultSelectionsPanelContext: SelectionsPanelContextType = {
  canCopyDeepLinkShareUrl: false,
  canCopyEmbedLink: false,
  canExportToCsv: false,
  canExportToKml: false,
  exportToCsv: unimplementedFunction,
  exportToKml: unimplementedFunction,
  getDeepLinkShareUrl: unimplementedAsyncFunction,
  getEmbedLink: unimplementedAsyncFunction,
  hasSelectedFeature: false,
  isAerialReportPanelOpen: false,
  isEmbedPanelOpen: false,
  isExportMenuOpen: false,
  isPanelOpen: true,
  selectedBoundaries: [],
  selectedComps: [],
  selectedCustomizations: [],
  selectedDeedFeatures: [],
  selectedFsa: [],
  selectedListings: [],
  selectedParcels: [],
  selectedSubjectProperties: [],
  selectedUploads: [],
  setIsAerialReportPanelOpen: unimplementedFunction,
  setIsEmbedPanelOpen: unimplementedFunction,
  setIsExportMenuOpen: unimplementedFunction,
  setIsPanelOpen: unimplementedFunction,
  totalSelectionCount: 0,
};

export const SelectionsPanelContext = createContext<SelectionsPanelContextType>(
  defaultSelectionsPanelContext
);

export const useSelectionsPanelContext = () =>
  useContext(SelectionsPanelContext);

type Props = PropsWithChildren<{}>;

export const SelectionsPanelProvider = ({ children = undefined }: Props) => {
  const params = useParams();
  const { track } = useAnalytics();

  const { userCanSaveSelection } = useUserQuotasContext();
  const {
    userCanExportCSV,
    hasActiveSession,
    userCanExportKML,
    userCanShareSelections,
  } = useAuth();
  const { activeFile } = useFileManagementContext();
  const { terrainEnabled } = useThreeDController();
  const { associatedCompsData } = useAssociatedComps(activeFile ?? null);

  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(true);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState<boolean>(false);
  const [isEmbedPanelOpen, setIsEmbedPanelOpen] = useState<boolean>(false);
  const [isAerialReportPanelOpen, setIsAerialReportPanelOpen] =
    useState<boolean>(false);

  const [hasSelectedFeature, setHasSelectedFeature] = useState<boolean>(false);

  const {
    customizations: { allCustomizations: selectedCustomizations },
  } = useCustomizeLandContext();

  const { validGroups: selectedDeedFeatures } = useDeedPlotterContext();

  const {
    parcels: selectedParcels,
    clus: selectedFsa,
    uploads: selectedUploads,
    saves: selectedSaves,
    claims: selectedClaims,
    comps: selectedComps,
    allFeatures,
    realEstates: selectedListings,
    boundaries: selectedBoundaries,
  } = useSelector((state: RootState) => state.features);

  const subjectProperties = useMemo(
    () => [
      ...(selectedParcels ?? []),
      ...(selectedFsa ?? []),
      ...(selectedUploads ?? []),
      ...(selectedSaves ?? []),
      ...(selectedClaims ?? []),
      ...(selectedBoundaries ?? []),
    ],
    [
      selectedBoundaries,
      selectedClaims,
      selectedFsa,
      selectedParcels,
      selectedSaves,
      selectedUploads,
    ]
  );

  const totalSelectionCount = useMemo(
    () =>
      allFeatures.length +
      (selectedCustomizations?.length || 0) +
      selectedDeedFeatures.length,
    [
      allFeatures.length,
      selectedCustomizations?.length,
      selectedDeedFeatures.length,
    ]
  );
  useEffect(() => {
    if (!totalSelectionCount) {
      setIsEmbedPanelOpen(false);
    }
  }, [totalSelectionCount]);

  const canExportToCsv = useMemo(() => {
    return (
      hasActiveSession &&
      userCanSaveSelection &&
      selectedComps.length <= 30 &&
      userCanExportCSV
    );
  }, [
    hasActiveSession,
    userCanSaveSelection,
    selectedComps.length,
    userCanExportCSV,
  ]);

  const exportToCsv = useCallback(async () => {
    if (!canExportToCsv) throw new Error('Not authorized to export to CSV');

    if (activeFile?.id) {
      const { res, err } = await downloadSelectionAsCsv(
        activeFile.id,
        !!subjectProperties.length
      );
      if (err) throw err;
      if (res) {
        track({
          data: { file_type: 'csv' },
          event: TrackingEvent.SAVED_SELECTION_EXPORTED,
        });
      }

      return;
    }

    await downloadCompsAndParcelsAsCsv(selectedParcels, selectedComps);
  }, [
    canExportToCsv,
    activeFile,
    selectedComps,
    selectedParcels,
    subjectProperties.length,
    track,
  ]);

  const canExportToKml = useMemo(() => {
    return userCanExportKML;
  }, [userCanExportKML]);

  const exportToKml = useCallback(async () => {
    if (!canExportToKml) throw new Error('Not authorized to export to KML');
    await exportKML(allFeatures);
    track({
      data: { file_type: 'kml' },
      event: TrackingEvent.SAVED_SELECTION_EXPORTED,
    });
  }, [allFeatures, canExportToKml, track]);

  const canCopyEmbedLink = useMemo(() => {
    return Boolean(
      userCanShareSelections &&
        totalSelectionCount - selectedListings.length > 0
    );
  }, [userCanShareSelections, totalSelectionCount, selectedListings.length]);

  const getEmbedLink = useCallback(
    async (
      {
        showElevation = true,
        showCropHistory = true,
        showSoils = true,
        set3dMode = terrainEnabled,
        showCustomizations = false,
      } = {
        set3dMode: terrainEnabled,
        showCropHistory: true,
        showCustomizations: false,
        showElevation: true,
        showSoils: true,
      }
    ) => {
      if (!canCopyEmbedLink) return undefined;

      let shareCode: string | undefined;

      if (activeFile) {
        const publicSavedSelection = await setSavedSelectionPublic(activeFile);
        if (!publicSavedSelection) return undefined;
      } else {
        shareCode = await buildShareCodeFromFeatures(allFeatures);
      }

      return buildEmbedLink({
        savedSelectionId: activeFile?.id,
        set3dMode,
        shareCode,
        showCropHistory,
        showCustomizations,
        showElevation,
        showSoils,
      });
    },
    [activeFile, allFeatures, canCopyEmbedLink, terrainEnabled]
  );

  const canCopyDeepLinkShareUrl = useMemo(() => {
    return Boolean(
      userCanShareSelections &&
        totalSelectionCount -
          (selectedListings.length + selectedCustomizations.length) >
          0
    );
  }, [
    selectedCustomizations.length,
    selectedListings.length,
    totalSelectionCount,
    userCanShareSelections,
  ]);

  const getDeepLinkShareUrl = useCallback(async () => {
    if (!canCopyDeepLinkShareUrl) return undefined;

    let shareCode: string | undefined;

    if (activeFile) {
      const publicSavedSelection = await setSavedSelectionPublic(activeFile);
      if (!publicSavedSelection) return undefined;

      shareCode = await buildShareCodeFromSelectionWithComps(
        allFeatures,
        publicSavedSelection,
        associatedCompsData
      );
    } else {
      shareCode = await buildShareCodeFromFeatures(allFeatures);
    }

    if (!shareCode) return undefined;

    const link = `${PLAT_MAP_URL}/${
      params?.layer || 'map'
    }?sharecode=${shareCode}`;

    return deepLink(link);
  }, [
    activeFile,
    allFeatures,
    associatedCompsData,
    canCopyDeepLinkShareUrl,
    params?.layer,
  ]);

  // This is used to determine if the user has selected any features
  // So we can hide the SEO expansion panel if they haven't selected anything
  useEffect(() => {
    if (totalSelectionCount > 0) {
      setHasSelectedFeature(true);
    }
  }, [totalSelectionCount]);

  const value = useMemo(() => {
    return {
      canCopyDeepLinkShareUrl,
      canCopyEmbedLink,
      canExportToCsv,
      canExportToKml,
      exportToCsv,
      exportToKml,
      getDeepLinkShareUrl,
      getEmbedLink,
      hasSelectedFeature,
      isAerialReportPanelOpen,
      isEmbedPanelOpen,
      isExportMenuOpen,
      isPanelOpen,
      selectedBoundaries,
      selectedComps,
      selectedCustomizations,
      selectedDeedFeatures,
      selectedFsa,
      selectedListings,
      selectedParcels,
      selectedSubjectProperties: subjectProperties,
      selectedUploads,
      setIsAerialReportPanelOpen,
      setIsEmbedPanelOpen,
      setIsExportMenuOpen,
      setIsPanelOpen,
      totalSelectionCount,
    };
  }, [
    canCopyDeepLinkShareUrl,
    canCopyEmbedLink,
    getDeepLinkShareUrl,
    getEmbedLink,
    canExportToCsv,
    subjectProperties,
    canExportToKml,
    exportToCsv,
    exportToKml,
    isEmbedPanelOpen,
    setIsEmbedPanelOpen,
    isExportMenuOpen,
    isPanelOpen,
    selectedBoundaries,
    selectedComps,
    selectedCustomizations,
    selectedDeedFeatures,
    selectedFsa,
    selectedListings,
    selectedParcels,
    selectedUploads,
    totalSelectionCount,
    isAerialReportPanelOpen,
    setIsAerialReportPanelOpen,
    hasSelectedFeature,
  ]);

  return (
    <SelectionsPanelContext.Provider value={value}>
      {children}
    </SelectionsPanelContext.Provider>
  );
};
